import { useCallback, useEffect, useRef, useState } from 'react';

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_MS = 13 * 60 * 1000; // 13 minutes (warning appears 2 min before logout)
const WARNING_DURATION_S = 120; // 2 minutes in seconds
const THROTTLE_MS = 1000;

interface UseInactivityTimeoutOptions {
  onLogout: () => void;
}

export function useInactivityTimeout({
  onLogout,
}: UseInactivityTimeoutOptions) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_DURATION_S);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current !== null) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current !== null) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current !== null) clearInterval(countdownRef.current);
  }, []);

  const startCountdown = useCallback(() => {
    setRemainingSeconds(WARNING_DURATION_S);
    if (countdownRef.current !== null) clearInterval(countdownRef.current);

    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (countdownRef.current !== null)
            clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startTimers = useCallback(() => {
    clearAllTimers();
    lastActivityRef.current = Date.now();

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, WARNING_MS);

    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      setShowWarning(false);
      onLogout();
    }, TIMEOUT_MS);
  }, [clearAllTimers, startCountdown, onLogout]);

  const resetTimers = useCallback(() => {
    setShowWarning(false);
    setRemainingSeconds(WARNING_DURATION_S);
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    startTimers();

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < THROTTLE_MS) return;
      lastActivityRef.current = now;

      // Only reset if warning is not already showing
      if (!warningTimerRef.current) return;
      if (document.querySelector('[data-session-warning]')) return;

      resetTimers();
    };

    const events: (keyof DocumentEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];

    events.forEach((event) =>
      document.addEventListener(event, handleActivity, true),
    );

    return () => {
      clearAllTimers();
      events.forEach((event) =>
        document.removeEventListener(event, handleActivity, true),
      );
    };
  }, [startTimers, resetTimers, clearAllTimers]);

  return { showWarning, remainingSeconds, resetTimers };
}
